export const EXTRA_FIELD_PREFIX = 'extrafield_'
export const CONSENT_FIELD_PREFIX = 'consent_'

export type ContextKey = 'backoffice' | 'public' | 'mobileApp'

export interface VisibilityConfig {
  display: boolean;
  required: boolean;
}

/**
 * One entry of a registration form's saved configuration. This is the shape that
 * round-trips through the hidden input to registration_forms.fields.
 */
export interface FieldConfig {
  name: string;
  backoffice: VisibilityConfig;
  public: VisibilityConfig;
  mobileApp: VisibilityConfig;
  required: boolean;
  settings?: Record<string, unknown>;
  /** Applied to the field only while every condition holds. */
  condition?: FieldConditionRule | null;
}

/**
 * One entry of the palette of fields that can be added to a form. Common fields
 * and consents carry defaults and lock information; extra fields only carry name and label.
 */
export interface AvailableField {
  name: string;
  label: string;
  default_visibility?: Partial<Record<ContextKey, VisibilityConfig>> & { required?: boolean };
  read_only_configuration?: Partial<Record<ContextKey, VisibilityConfig>> & {
    required?: boolean;
    mandatory?: boolean;
  };
  settings?: TemplateOptionDefinition[];
  /** What showing the field in a place means, told under that place. */
  place_notes?: Partial<Record<ContextKey, string>>;
  /** Fields this one cannot work without; the form cannot be saved while one is missing. */
  requires?: RequiredField[];
  condition_source?: ConditionSource | null;
}

export interface RequiredField {
  name: string;
  label: string;
}

export interface ConditionChoice {
  value: string;
  label: string;
}

/** What a field offers when another field's rule reads it. */
export interface ConditionSource {
  operations: ConditionChoice[];
  /** Empty for age comparisons, which take a number of years instead. */
  options: ConditionChoice[];
}

export interface Condition {
  field: string;
  operation: string;
  value: string | number;
}

export type ConditionEffect = 'show' | 'require'

/** A field's rule: what to do to it, and the conditions that must all hold. */
export interface FieldConditionRule {
  effect: ConditionEffect;
  all: Condition[];
}

export const AGE_OPERATIONS = ['ageUnder', 'ageAtLeast']

export const isAgeOperation = (operation: string): boolean => AGE_OPERATIONS.includes(operation)

export const DEFAULT_AGE = 18

export interface TemplateOptionDefinition {
  name: string;
  label?: string;
  type?: string;
  [key: string]: unknown;
}

export const isExtraField = (name: string): boolean => name.startsWith(EXTRA_FIELD_PREFIX)
export const isConsentField = (name: string): boolean => name.startsWith(CONSENT_FIELD_PREFIX)

/**
 * What a field does in one place. Stored as two booleans, but only three of the
 * four combinations are meaningful: a field cannot be required and hidden.
 */
export type FieldState = 'hidden' | 'optional' | 'required'

export const toFieldState = (visibility?: VisibilityConfig): FieldState => {
  if (!visibility?.display) {
    return 'hidden'
  }

  return visibility.required ? 'required' : 'optional'
}

export const fromFieldState = (state: FieldState): VisibilityConfig => ({
  display: state !== 'hidden',
  required: state === 'required',
})
